import {
    type AppliedStyles,
    BoundedText,
    type BoundedTextProps,
    deriveAppliedStylesFromStyleProps,
    Direction,
    getFontFamilies,
    getTextDirections,
    HorizontalAlignment,
    type IStructuredGlyph,
    Placement,
    type RunProps,
    ShapedText,
    TextConfig,
    type TextEditorData,
    type TextProps,
    TextRenderer,
    VerticalAlignment,
} from '@bynder-studio/structured-text';
import {
    BorderAlignmentTypes,
    ColorParams,
    defaultColor,
    ORDINARY_STROKE_COLOR_OBJ,
    StrokeType,
    TextBackground,
    TRANSPARENT_COLOR,
} from '@bynder-studio/misc';
import { getTextPropsFonts } from '@bynder-studio/structured-text/src/Helpers/textUtils';
import { ElementUpdateTypes } from '../../Enums/ElementUpdateTypes';
import { MaskModeTypes } from '../../Enums/MaskModeTypes';
import { horizontalAlignment, verticalAlignment } from '../../Enums/TextContentTransform';
import { createAnimationIn } from '../Animations/AnimationIn';
import { createAnimationOut } from '../Animations/AnimationOut';
import { createRectanglePath, generateCurvedPath } from '../../Helpers/pathFunctions';
import { FontAsset } from '../Assets/FontAsset';
import type { IAsset } from '../Assets/IAsset';
import { BaseCompElement } from '../CompModels/Elements/BaseCompElement';
import { GroupCompElement } from '../CompModels/Elements/GroupCompElement';
import { SvgCompElement, SvgCompElementPool } from '../CompModels/Elements/SvgCompElement';
import { ShapeCompElement, type ShapeCompElementParams } from '../CompModels/Elements/ShapeCompElement';
import { Box } from '../Shared/Box';
import { Mask } from '../Shared/Mask';
import { Region } from '../Shared/Region';
import { ContentTransform } from '../Shared/ContentTransform';
import { Dimension } from '../Shared/Dimension';
import { Font } from '../Shared/Font';
import { Position } from '../Shared/Position';
import { BaseVisualElement } from './BaseVisualElement';
import { TimelineBehavior } from '../../Enums/TimelineBehavior';
import { LeadingTypes } from '../../Enums/LeadingTypes';
import { CreativeTypes } from '../../Enums/CreativeTypes';
import { PreviewTypes } from '../../Enums/PreviewTypes';
import { deepClone, equals } from '../../Helpers/utils';
import { TextStyles } from '../Shared/TextStyles';
import { getNextVisibleLineIndex, getPrevVisibleLineIndex, getShapedLinesBoxesWithBg } from './Helpers/TextHelpers';
import { type ElementUpdateOptions, Reason, type TextElementParams, TextStyle } from '../../types';
import type { BaseAsset } from '../Assets/BaseAsset';

import { IMAGE_MAX_CHARACTERS, VIDEO_MAX_CHARACTERS } from '../../constants';

export class TextElement extends BaseVisualElement {
    charCount!: number;

    formattedText!: TextProps;

    textDirection?: Direction;

    detectedTextDirections: { ltr: number; rtl: number; detectedDirection: Direction } = {
        ltr: 0,
        rtl: 0,
        detectedDirection: Direction.LTR,
    };

    boundedText!: BoundedText;

    textControl?: string;

    /** @deprecated Use getTextProps instead */
    font: Font;

    minFontScale: number = null;

    fontScale = 1;

    leadingType: LeadingTypes;

    glyphData: ShapedText;

    showOversetBox = false;

    limitTextToBounds = false;

    compOversetBox: GroupCompElement[] = [];

    compSvgText: GroupCompElement | null;

    compSvgData: GroupCompElement | null;

    compSvgLines: GroupCompElement[];

    compSvgWords: GroupCompElement[];

    compSvgChars!: SvgCompElement[];

    colorRegions: Map<
        SvgCompElement,
        {
            region: Region;
            color: ColorParams;
            subregions: Region[];
            lineIds: Set<GroupCompElement['id']>;
        }
    >;

    gradients: GroupCompElement | undefined;

    backgrounds: GroupCompElement | undefined;

    backgroundsBorders: GroupCompElement | undefined;

    bgGradients: GroupCompElement | undefined;

    bgMergeMap: Map<number, number> = new Map();

    timelineBehavior: TimelineBehavior = TimelineBehavior.AUTO;

    contentTransform: ContentTransform | null = null;

    textBackground: TextBackground | null = null;

    textEditorData: TextEditorData | null = null;

    isTextEditorActivated = false;

    textStyles: string[] = [];

    brandColors: number[] = [];

    textBackgroundBrandColors: number[] = [];

    private textStylesInstance: TextStyles;

    private readonly svgCompElementPool = new SvgCompElementPool();

    private glyphPathBboxCache: { [key: string]: any } = {};

    constructor(params: Partial<TextElementParams & { showOversetBox: boolean }>) {
        super();
        // this.glyphData = { lines: [] };
        // todo: remove this when we have removed all font usage
        this.font = new Font({
            fontId: 'default',
            fontSize: 50,
            fontColor: { type: 'SOLID', red: 0, green: 0, blue: 0, opacity: 1, brandColorId: null },
            lineHeight: 1,
            charSpacing: 0,
        });

        this.setProperties(params);
    }

    static servicePropsList = [
        'updateTextForSelection',
        'updateTextSettingsByEachRun',
        'updateTextSettingForSelection',
    ];

    setTextStyles(textStyles: TextStyles) {
        this.textStylesInstance = textStyles;
    }

    setProperties(params: Partial<TextElementParams & { showOversetBox: boolean }>): Set<ElementUpdateTypes> {
        const updateTypes: Set<ElementUpdateTypes> = super.setProperties(params);

        if (params.textControl !== undefined) {
            this.textControl = params.textControl;
            updateTypes.add(ElementUpdateTypes.TEXT_CONTROL);
        }

        if (params.minFontScale !== undefined) {
            this.minFontScale = params.minFontScale;
            updateTypes.add(ElementUpdateTypes.MIN_FONT_SCALE);
        }

        if (params.fontScale !== undefined) {
            this.fontScale = params.fontScale;
            updateTypes.add(ElementUpdateTypes.FONT_SCALE);
        }

        if (params.limitTextToBounds !== undefined) {
            this.limitTextToBounds = params.limitTextToBounds;
            updateTypes.add(ElementUpdateTypes.LIMIT_TEXT_TO_BOUNDS);
        }

        if (params.showOversetBox !== undefined) {
            this.showOversetBox = params.showOversetBox;
            updateTypes.add(ElementUpdateTypes.SHOW_OVERSET_BOX);
        }

        if (params.textBackground !== undefined) {
            this.textBackground = params.textBackground ? new TextBackground(params.textBackground) : null;
            updateTypes.add(ElementUpdateTypes.TEXT_BACKGROUND);
        }

        if (
            params.timelineBehavior !== undefined &&
            this.timelineBehavior !== params.timelineBehavior &&
            Object.values(TimelineBehavior).includes(params.timelineBehavior)
        ) {
            this.timelineBehavior = params.timelineBehavior || TimelineBehavior.AUTO;
            updateTypes.add(ElementUpdateTypes.TIMELINE_BEHAVIOR);
        }

        if (params.contentTransform !== undefined && params.contentTransform !== null) {
            this.contentTransform = new ContentTransform(params.contentTransform);
            updateTypes.add(ElementUpdateTypes.CONTENT_TRANSFORM);
        }

        if (params.formattedText !== undefined && !equals(this.formattedText, params.formattedText)) {
            if (this.formattedText?.value !== params.formattedText?.value) {
                this.svgCompElementPool.clear();
                updateTypes.add(ElementUpdateTypes.TEXT);
            } else {
                updateTypes.add(ElementUpdateTypes.TEXT_RUNS);
            }

            this.formattedText = params.formattedText;
            const detectedTextDirections = getTextDirections(this.formattedText.value);

            if (!equals(detectedTextDirections, this.detectedTextDirections)) {
                this.detectedTextDirections = detectedTextDirections;
                updateTypes.add(ElementUpdateTypes.TEXT_DIRECTION);
            }
        }

        if (params.textDirection !== undefined && this.textDirection !== params.textDirection) {
            this.textDirection = params.textDirection;
            updateTypes.add(ElementUpdateTypes.TEXT_DIRECTION);
        }

        if (params.textStyles !== undefined && !equals(this.textStyles, params.textStyles)) {
            this.textStyles = params.textStyles;
            updateTypes.add(ElementUpdateTypes.TEXT_STYLES);
        }

        if (params.brandColors !== undefined && !equals(this.brandColors, params.brandColors)) {
            this.brandColors = params.brandColors;
            updateTypes.add(ElementUpdateTypes.TEXT_BRAND_COLORS);
        }

        if (
            params.textBackgroundBrandColors !== undefined &&
            !equals(this.textBackgroundBrandColors, params.textBackgroundBrandColors)
        ) {
            this.textBackgroundBrandColors = params.textBackgroundBrandColors;
            updateTypes.add(ElementUpdateTypes.TEXT_BACKGROUND_BRAND_COLORS);
        }

        if (this.textEditorData !== params.textEditorData && params.textEditorData !== undefined) {
            this.textEditorData = params.textEditorData;
            updateTypes.add(ElementUpdateTypes.TEXT_EDITOR_DATA);
        }

        this.leadingType = params.leadingType ?? this.leadingType;
        // todo: check do we actually use this?
        this.glyphData = params.glyphData || this.glyphData;

        return updateTypes;
    }

    specificUpdateDataProcessing(rawElement: any, reason: Reason) {
        if (rawElement.showOversetBox && reason !== 'user') {
            delete rawElement.showOversetBox;
        }

        if (rawElement.isTextEditorActivated && reason !== 'user') {
            delete rawElement.isTextEditorActivated;
        }

        if (rawElement.updateTextForSelection) {
            const { start, end, diff, updateLayout } = rawElement.updateTextForSelection;

            rawElement.formattedText = this.updateTextForSelection(start, end, diff, !!updateLayout);
            delete rawElement.updateTextForSelection;
        }

        if (rawElement.updateTextSettingsByEachRun) {
            const { styles } = rawElement.updateTextSettingsByEachRun;
            let startIdx = 0;
            let endIdx = this.boundedText.getShapedText().getText().length;

            if (this.textEditorData?.selection) {
                const { selection } = this.textEditorData;
                [startIdx, endIdx] = this.boundedText
                    .getShapedText()
                    .getTextIdxsFromSelection(selection.start, selection.end);
            }

            rawElement.formattedText = this.updateTextSettingsByEachRun(startIdx, endIdx, styles);
            delete rawElement.updateTextSettingsByEachRun;
        }

        if (rawElement.updateTextSettingForSelection) {
            const { start, end, settings } = rawElement.updateTextSettingForSelection;
            this.textStylesInstance.applyStyle(settings);

            const isCorrectIdx = (idx: number | undefined) =>
                Number.isInteger(idx) &&
                (idx as number) >= 0 &&
                (idx as number) <= this.boundedText.getShapedText().getText().length;

            let startIdx = isCorrectIdx(start) ? start : 0;
            let endIdx = isCorrectIdx(end) ? end : this.boundedText.getShapedText().getText().length;

            if (this.textEditorData !== null) {
                const { selection, cursorPosition } = this.textEditorData;
                [startIdx, endIdx] = this.boundedText
                    .getShapedText()
                    .getTextIdxsFromSelection(selection.start ?? cursorPosition, selection.end ?? cursorPosition);
            }

            rawElement.formattedText = this.updateTextSettingsForSelection(startIdx, endIdx, settings);
            delete rawElement.updateTextForSelection;
        }

        return rawElement;
    }

    update(
        params: Partial<
            TextElementParams & {
                showOversetBox: boolean;
            }
        >,
        options?: ElementUpdateOptions,
    ): Set<ElementUpdateTypes> {
        const { frameRate = 25 } = options || {};
        const oldFormattedText = deepClone(this.formattedText);
        const oldTextControl = this.textControl;
        const oldContentTransform = this.contentTransform;
        const oldDimension = this.dimension;
        const oldMinFontScale = this.minFontScale;
        const oldFontScale = this.fontScale;
        const oldTextDirection = this.textDirection || this.detectedTextDirections.detectedDirection;
        const oldTextBg = deepClone(this.textBackground);
        const updateTypes: Set<ElementUpdateTypes> = this.setProperties(params);
        const newTextDirection = this.textDirection || this.detectedTextDirections.detectedDirection;

        if (updateTypes.size > 2 || (updateTypes.size && !updateTypes.has(ElementUpdateTypes.POSITION))) {
            this.compSvgData = null;
        }

        if (this.leadingType === LeadingTypes.EXTERNAL_LEADING_PCT) {
            // conversion here is mainly used for tests where textelements are updated with another leadingtype
            const { runs } = this.getTextProps();

            this.formattedText.runs = runs.map((item) => {
                const leading = TextRenderer.getInstance().convertLeadingValue(item.fontId, item.leading);

                return { ...item, leading };
            });

            this.leadingType = LeadingTypes.LEADING_PCT;
        }

        if (params.isTextEditorActivated !== undefined && this.isTextEditorActivated !== params.isTextEditorActivated) {
            this.isTextEditorActivated = params.isTextEditorActivated;
        }

        if (params.showOversetBox !== undefined && this.showOversetBox !== params.showOversetBox) {
            this.showOversetBox = params.showOversetBox;
            updateTypes.add(ElementUpdateTypes.SHOW_OVERSET_BOX);
        }

        if (params.limitTextToBounds !== undefined && this.limitTextToBounds !== params.limitTextToBounds) {
            this.limitTextToBounds = params.limitTextToBounds;
            updateTypes.add(ElementUpdateTypes.LIMIT_TEXT_TO_BOUNDS);
        }

        if (this.textEditorData !== params.textEditorData && params.textEditorData !== undefined) {
            this.textEditorData = params.textEditorData;
            updateTypes.add(ElementUpdateTypes.TEXT_EDITOR_DATA);
        }

        if (params.textBackground !== undefined && !equals(oldTextBg, params.textBackground)) {
            this.textBackground = params.textBackground ? new TextBackground(params.textBackground) : null;
            updateTypes.add(ElementUpdateTypes.TEXT_BACKGROUND);
        }

        if (oldTextControl !== this.textControl) {
            this.minFontScale = null;
        }

        if (
            !equals(oldFormattedText, this.formattedText) ||
            !equals(oldTextBg, this.textBackground) ||
            !this.dimension.equals(oldDimension) ||
            this.contentTransform !== oldContentTransform ||
            this.textControl !== oldTextControl ||
            this.minFontScale !== oldMinFontScale ||
            this.fontScale !== oldFontScale ||
            newTextDirection !== oldTextDirection
        ) {
            const renderConfigs: Partial<TextConfig> = {};

            if (this.dimension !== oldDimension) {
                renderConfigs.boxWidth = this.dimension.getWidth();
                renderConfigs.boxHeight = this.dimension.getHeight();
            }

            if (this.minFontScale !== oldMinFontScale) {
                renderConfigs.minFontScale = this.minFontScale;
            }

            if (this.contentTransform !== oldContentTransform) {
                renderConfigs.xAlign =
                    horizontalAlignment[parseInt(this.contentTransform!.horizontalAlignment as unknown as string)];
                renderConfigs.yAlign =
                    verticalAlignment[parseInt(this.contentTransform!.verticalAlignment as unknown as string)];
            }

            if (this.textControl !== oldTextControl) {
                renderConfigs.placement = this.textControl as Placement;
            }

            if (!equals(this.textBackground, oldTextBg)) {
                renderConfigs.textBackground = this.textBackground;
            }

            if (newTextDirection !== oldTextDirection) {
                renderConfigs.textDirection = newTextDirection;
            }

            if (this.isThereFontsToLoad()) {
                this.constructAsset(frameRate);
            }

            this.boundedText.updateConfig(renderConfigs);
            const boxProps = this.getBoundedTextProps();
            boxProps.textStruct = this.formattedText;
            this.boundedText.updateConfig(boxProps);
            this.compSvgData = null;
            this.setInitialMinFontScale();

            if (renderConfigs.placement || renderConfigs.minFontScale) {
                this.updateCursorPosition();
            }
        }

        return updateTypes;
    }

    private updateCursorPosition() {
        if (this.textEditorData?.cursorPosition) {
            this.textEditorData.cursorPosition = this.boundedText.getShapedTextPositionByGlyphIdx(
                this.textEditorData.cursorPosition.glyphIdx,
            );
        }
    }

    private setInitialMinFontScale() {
        if (this.minFontScale === null) {
            this.minFontScale = this.boundedText.getAutoResizeScale();
        }
    }

    init() {
        const textRenderer = TextRenderer.getInstance();
        this.boundedText = textRenderer.createBoundedText(this.getTextProps(), this.getBoundedTextProps());
    }

    getTextProps(): TextProps {
        return this.formattedText;
    }

    getTextSettingForSelection(start?: number, end?: number, onlyUnique = true) {
        return this.boundedText.getShapedText().getStylesForSelection(start, end, onlyUnique);
    }

    updateTextForSelection(start: number, end: number, text: string, updateLayout = false) {
        return this.boundedText.getShapedText().updateTextForSelection(start, end, text, updateLayout);
    }

    updateTextSettingsForSelection(start: number, end: number, styles: Omit<RunProps, 'length'>) {
        return this.boundedText.getShapedText().updateStylesForSelection(start, end, styles);
    }

    updateTextSettingsByEachRun(start: number, end: number, styles: Omit<RunProps, 'length'>[]) {
        return this.boundedText.getShapedText().updateStylesForEachRun(start, end, styles);
    }

    getCurrentTextSettings(onlyUnique = true): AppliedStyles {
        const shapedText = this.boundedText.getShapedText();

        if (shapedText) {
            const selection = !this.isTextEditorActivated ? null : this.textEditorData?.selection;
            const cursorPosition = !this.isTextEditorActivated ? null : this.textEditorData?.cursorPosition;

            const start = selection?.start ?? cursorPosition;
            const end = selection?.end ?? cursorPosition;

            const textIdxs: [number, number] =
                start && end ? shapedText.getTextIdxsFromSelection(start, end) : [0, this.getTextProps().value.length];

            return shapedText.getStylesForSelection(...textIdxs, onlyUnique);
        }

        // todo: derive from all runs
        return deriveAppliedStylesFromStyleProps(this.getTextProps().runs[0]);
    }

    createTextStyle(name: string, options?: { forceDefaultOutline?: boolean }): string {
        const settings = this.getCurrentTextSettings();
        const style = { name };
        let isPossible = true;

        Object.entries(settings).forEach(([key, value]) => {
            if (['styleId', 'color'].includes(key)) {
                return;
            }

            isPossible = value.length === 1;

            if (isPossible) {
                style[key] = value[0];
            }
        });

        if (!isPossible) {
            return null;
        }

        if (options?.forceDefaultOutline && style['stroke']) {
            style['stroke'] = { ...style['stroke'], strokeColor: defaultColor, punchOut: false };
        }

        return this.textStylesInstance.createStyle(style as TextStyle);
    }

    /**
     * Convert text element properties into bounded text configuration
     * @returns
     */
    getBoundedTextProps(): BoundedTextProps {
        const xAlignStr =
            horizontalAlignment[
                parseInt(this.contentTransform!.horizontalAlignment as unknown as string)
            ].toUpperCase();
        const yAlignStr =
            verticalAlignment[parseInt(this.contentTransform!.verticalAlignment as unknown as string)].toUpperCase();

        return {
            placement: Placement[this.textControl], // ?.toLowerCase(),
            boxWidth: this.dimension!.getWidth(),
            boxHeight: this.dimension!.getHeight(),
            horizontalAlignment: HorizontalAlignment[xAlignStr],
            verticalAlignment: VerticalAlignment[yAlignStr],
            minFontScale: this.minFontScale,
            fontScale: this.fontScale,
            textBackground: this.textBackground,
            textDirection: this.textDirection || this.detectedTextDirections.detectedDirection,
            // tracking: this.font.charSpacing,
            // leading: leading, //TODO: Enable when we support this in shaper
        };
    }

    constructAsset(frameRate: number = 0): void {
        const fontsToLoad = getTextPropsFonts(this.formattedText, getFontFamilies());
        const textRenderer = TextRenderer.getInstance();
        const { runs } = this.getTextProps();
        let loadingCount = 0;

        Object.keys(fontsToLoad).map((fontId) => {
            const inList = this.assetLoader!.getAsset(fontId);
            const asset = new FontAsset({
                id: fontId,
                fontId,
                src: fontsToLoad[fontId],
            });
            this.isAssetLoading = !!++loadingCount;
            this.assetLoader!.setAsset(asset);
            this.assetLoader!.loadFont(asset)
                .then((asset) => {
                    if (asset.object) {
                        if (!inList) {
                            textRenderer.setFont(fontId.toString(), asset.object, fontsToLoad[fontId]);
                        }

                        // TODO: check if font leading check is necessary here
                        if (this.leadingType === LeadingTypes.EXTERNAL_LEADING_PCT) {
                            this.formattedText.runs = runs.map((run) => {
                                const leading = textRenderer.convertLeadingValue(run.fontId, run.leading);
                                run.leading = leading;

                                return { ...run, leading };
                            });

                            this.leadingType = LeadingTypes.LEADING_PCT;
                        }

                        const boxProps = this.getBoundedTextProps();
                        boxProps.textStruct = this.formattedText;
                        this.boundedText.updateConfig(boxProps, true);
                        this.compSvgData = null;
                    }
                })
                .finally(() => {
                    this.isAssetLoading = !!--loadingCount;
                });
        });
    }

    isContainsAsset(asset: IAsset): boolean {
        return asset instanceof FontAsset && !!getTextPropsFonts(this.formattedText, getFontFamilies())[asset.fontId];
    }

    private constructCompElement(frameIndex: number) {
        // TODO: do this during setup (note that since contents changes, we might need to copy for each frame)
        // Prepare SVG comp structure
        // Organise glyphs into lines, words and characters
        // Important note: Make sure that IDs for all of these elements are unique, they are used to match between frames.

        // TODO: logic is breaked after wrapping/unwrapping, improve it later
        // if (!this.isAnimated() && !this.hasAnimatedNeighbor() && this.compSvgData) {
        //     return this.compSvgData;
        // }

        if (this.compSvgChars?.length > 0) {
            this.svgCompElementPool.releaseAll(this.compSvgChars, frameIndex);
        }

        this.compSvgData = null;
        this.compSvgLines = []; // GroupCompElements
        this.compSvgWords = []; // GroupCompElements
        this.compSvgChars = []; // SvgCompElements
        this.compOversetBox = [];
        this.colorRegions = new Map();
        this.gradients = null;
        this.backgrounds = null;
        this.backgroundsBorders = null;
        this.bgGradients = null;
        this.bgMergeMap = new Map();

        const glyphData = this.boundedText.getShapedText();
        const shapedLines = glyphData.getShapedLines();

        const isSpace = (glyph: IStructuredGlyph) => glyph.data.text.length === 1 && glyph.data.text[0] === ' ';
        const isBreak = (glyph: IStructuredGlyph) => glyph.data.text.length === 1 && glyph.data.text[0] === '\n';
        const isPrevSpace = (glyphs: IStructuredGlyph[], index: number) => index > 0 && isSpace(glyphs[index - 1]);
        const isDecorated = (glyph: IStructuredGlyph) => !!glyph.data?.decorationPaths?.length;

        let requireTempLayer = false;
        let prevCharColor: ColorParams | null = null;
        let prevGlyphCompElement: SvgCompElement | null = null;

        const getColorMaskData = (color: ColorParams, region: Region, compElement: SvgCompElement) => {
            if (equals(prevCharColor, color) && this.colorRegions.has(prevGlyphCompElement)) {
                const data = this.colorRegions.get(prevGlyphCompElement);

                this.colorRegions.set(compElement, data);
                prevGlyphCompElement = compElement;

                return data;
            }

            prevCharColor = color;
            prevGlyphCompElement = compElement;

            const data = {
                region,
                color,
                subregions: [],
                lineIds: new Set<GroupCompElement['id']>(),
            };

            this.colorRegions.set(compElement, data);

            return data;
        };

        const getPaintedRegion = (glyph: IStructuredGlyph, glyphCompElement: SvgCompElement) => {
            const stroke = this.boundedText.getShapedText().getTextPropsProperty('stroke', glyph.data.runIdx);
            const region = glyphCompElement.boundingBox.toRegion();

            if (stroke.width > 0 && (stroke.type === StrokeType.OUTSIDE || stroke.type === StrokeType.CENTER)) {
                const offset = stroke.width / (stroke.type === StrokeType.CENTER ? 2 : 1);

                region.top = Math.floor(region.top - offset);
                region.left = Math.floor(region.left - offset);
                region.right = Math.ceil(region.right + offset);
                region.bottom = Math.ceil(region.bottom + offset);
            }

            return region;
        };

        const createColorShapes = (glyph: IStructuredGlyph, glyphCompElement: SvgCompElement) => {
            if ((isSpace(glyph) && !isDecorated(glyph)) || isBreak(glyph)) {
                return;
            }

            const color = this.boundedText.getShapedText().getTextPropsProperty('color', glyph.data.runIdx);
            const region = getPaintedRegion(glyph, glyphCompElement);
            const maskData = getColorMaskData(color, region, glyphCompElement);

            const updatedRegion = maskData.region.getUnion(region);

            maskData.region = updatedRegion;
            maskData.subregions.push(region);
        };

        const createSpanCollector = (getRunColor: (runIdx: number) => ColorParams | null | undefined) => {
            let prevColor: ColorParams | null = null;
            let prevSpan: { region: Region } | null = null;
            const spans = new Map<SvgCompElement, { span: { region: Region }; origin: Position }>();

            return {
                collect: (glyph: IStructuredGlyph, glyphCompElement: SvgCompElement) => {
                    if ((isSpace(glyph) && !isDecorated(glyph)) || isBreak(glyph)) {
                        return;
                    }

                    const color = getRunColor(glyph.data.runIdx);

                    if (!color || color.type === 'SOLID') {
                        return;
                    }

                    const region = getPaintedRegion(glyph, glyphCompElement);

                    if (equals(prevColor, color) && prevSpan) {
                        prevSpan.region = prevSpan.region.getUnion(region);
                    } else {
                        prevColor = color;
                        prevSpan = { region };
                    }

                    const strokeOverflow =
                        glyphCompElement.strokeType === StrokeType.OUTSIDE ? glyphCompElement.strokeWidth : 0;

                    spans.set(glyphCompElement, {
                        span: prevSpan,
                        origin: new Position(
                            glyphCompElement.boundingBox.position.getX() - strokeOverflow,
                            glyphCompElement.boundingBox.position.getY() - strokeOverflow,
                        ),
                    });
                },
                assign: (setRegion: (compEl: SvgCompElement, region: Region) => void) => {
                    spans.forEach(({ span, origin }, compEl) => {
                        setRegion(compEl, span.region.getTranslation(-origin.getX(), -origin.getY()));
                    });
                },
            };
        };

        const textProps = this.boundedText.getShapedText();
        const strokeSpans = createSpanCollector((runIdx) => {
            const stroke = textProps.getTextPropsProperty('stroke', runIdx);

            return stroke?.width && stroke.type !== StrokeType.NONE ? stroke.strokeColor : null;
        });
        const fillSpans = createSpanCollector((runIdx) => textProps.getTextPropsProperty('color', runIdx));

        const hasGradients = this.formattedText.runs.some((run) => run.color.type !== 'SOLID');
        const hasStrokeGradients = this.formattedText.runs.some(
            (run) => !!run.stroke?.strokeColor && run.stroke.strokeColor.type !== 'SOLID',
        );

        const hasOutlineColors = this.formattedText.runs.some(
            (run) => !!run.stroke?.strokeColor && !!run.stroke.width && run.stroke.type !== StrokeType.NONE,
        );
        const paintFillGradientsDirectly = hasGradients && hasOutlineColors;

        // (currentIsSpace && !prevIsSpace) || (prevIsSpace && !currentIsSpace)
        const flushWord = (compWord: SvgCompElement[], compLine: GroupCompElement[]) => {
            if (compWord.length === 0) {
                return;
            }

            const svgWordEl = new GroupCompElement(compWord);
            svgWordEl.id = this.id + '_' + this.compSvgLines.length + '_' + this.compSvgWords.length; // unique ID

            svgWordEl.name = 'word_' + svgWordEl.id;
            svgWordEl.renderOrder = this.renderOrder;
            this.compSvgWords.push(svgWordEl);
            compLine.push(svgWordEl);
        };

        const shouldStartWordWith = (glyph: IStructuredGlyph, isSpaceForBreak: boolean) =>
            !isSpaceForBreak &&
            (glyph.data.path !== '' || glyph.data.decorationPaths || (this.textBackground && !isBreak(glyph)));

        shapedLines.forEach((line, lineIdx) => {
            const compLine: GroupCompElement[] = [];
            let compWord: SvgCompElement[] = [];
            line.glyphs.forEach((glyph, index) => {
                const svgCompEl = this.getSvgCompElement(glyph, frameIndex, paintFillGradientsDirectly);

                if (hasGradients && !paintFillGradientsDirectly) {
                    createColorShapes(glyph, svgCompEl);
                }

                if (paintFillGradientsDirectly) {
                    fillSpans.collect(glyph, svgCompEl);
                }

                if (hasStrokeGradients) {
                    strokeSpans.collect(glyph, svgCompEl);
                }

                this.compSvgChars.push(svgCompEl);

                if (!requireTempLayer && (glyph.data?.strokeType === StrokeType.OUTSIDE || glyph.data?.punchOut)) {
                    requireTempLayer = true;
                }

                const currentIsSpace = isSpace(glyph);
                const prevIsSpace = isPrevSpace(line.glyphs, index);
                const isSpaceForBreak =
                    currentIsSpace && index === line.glyphs.length - 1 && lineIdx !== shapedLines.length - 1;

                if (isBreak(glyph) || (currentIsSpace && !prevIsSpace) || (prevIsSpace && !currentIsSpace)) {
                    flushWord(compWord, compLine);
                    compWord = [];

                    if (shouldStartWordWith(glyph, isSpaceForBreak)) {
                        compWord.push(svgCompEl);
                    }
                } else {
                    compWord.push(svgCompEl);
                }
            });

            flushWord(compWord, compLine);

            if (compLine.length > 0) {
                const compSvgLine = new GroupCompElement(compLine);
                compSvgLine.id = this.id + '_' + this.compSvgLines.length; // unique ID

                compSvgLine.name = 'line_' + compSvgLine.id;
                compSvgLine.renderOrder = this.renderOrder;

                if (hasGradients && !paintFillGradientsDirectly) {
                    // the line is used as a mask for the text gradient
                    compSvgLine.usedAsInternalMask = true;

                    // add the line id to containing glyphs
                    // will be used to create a mask
                    compLine
                        .flatMap((word) => (word as GroupCompElement).getChildren())
                        .forEach((char) => {
                            this.colorRegions.get(char as SvgCompElement)?.lineIds.add(compSvgLine.id);
                        });
                }

                this.compSvgLines.push(compSvgLine);
            }
        });

        strokeSpans.assign((compEl, region) => {
            compEl.strokeGradientRegion = region;
        });
        fillSpans.assign((compEl, region) => {
            compEl.colorGradientRegion = region;
        });

        if (this.showOversetBox && this.limitTextToBounds) {
            const oversetBox = new GroupCompElement([this.getOversetCompBox()]);

            oversetBox.id = `${this.id}_overset_comp_box${this.compOversetBox.length}`; // unique ID
            oversetBox.name = `overset_box_${oversetBox.id}`;
            oversetBox.renderOrder = this.renderOrder;

            this.compOversetBox.push(oversetBox);
        }

        const bgCompElements = this.generateBgCompElements();
        const hasBgGradient = this.textBackground?.isActive() && this.textBackground.color.type !== 'SOLID';

        if (bgCompElements.length) {
            this.backgrounds = new GroupCompElement(bgCompElements);

            this.backgrounds.id = `${this.id}_background`;
            this.backgrounds.name = `background_${this.backgrounds.id}`;
            this.backgrounds.renderOrder = this.renderOrder;
            this.backgrounds.usedAsInternalMask = hasBgGradient;

            if (hasBgGradient) {
                this.backgroundsBorders = new GroupCompElement(
                    this.generateBgCompElements().map((bgCompElement) => {
                        const borderCompElementParams = bgCompElement.toObject() as unknown as ShapeCompElementParams;
                        borderCompElementParams.id += '_border';
                        borderCompElementParams.name += '_border';
                        borderCompElementParams.color = TRANSPARENT_COLOR;
                        const borderCompElement = new ShapeCompElement();
                        borderCompElement.setProperties(borderCompElementParams);

                        return borderCompElement;
                    }),
                );
                this.backgroundsBorders.id = `${this.id}_background_borders`;
                this.backgroundsBorders.name = `background_borders_${this.backgrounds.id}`;
                this.backgrounds.renderOrder = this.renderOrder;
            }
        }

        const textGradientsEls = [...new Set(this.colorRegions.values())]
            .flatMap((data) => {
                // separate the data for each line
                return [...data.lineIds].map((lineId) => ({
                    region: data.region,
                    color: data.color,
                    subregions: data.subregions,
                    lineId,
                }));
            })
            .map(({ region, color, subregions, lineId }, i) => {
                const textGradient = new SvgCompElement();

                textGradient.id = `${this.id}_text_gradient__${i}`;
                textGradient.color = color;
                textGradient.isAssetLoading = this.isAssetLoading;
                textGradient.renderOrder = this.renderOrder;
                textGradient.pathPosition = new Position(0, 0);

                const dimensions = new Dimension(region.getWidth(), region.getHeight());

                textGradient.path = subregions.reduce((path, subregion) => {
                    const rect = createRectanglePath(
                        subregion.left - region.left,
                        subregion.top - region.top,
                        subregion.getWidth(),
                        subregion.getHeight(),
                    );

                    return `${path} ${rect}`;
                }, '');
                textGradient.contentBox = new Box(new Position(0, 0), dimensions.getCopy());
                textGradient.boundingBox = new Box(new Position(region.left, region.top), dimensions);

                textGradient.mask = new Mask({
                    elementId: lineId as number,
                    mode: MaskModeTypes.ALPHA,
                });

                return textGradient;
            });

        if (hasBgGradient) {
            const bgGradient = new SvgCompElement();

            bgGradient.id = `${this.id}_bg_gradient`;
            bgGradient.color = this.textBackground.color;
            bgGradient.isAssetLoading = this.isAssetLoading;
            bgGradient.renderOrder = this.renderOrder;
            bgGradient.pathPosition = new Position(0, 0);

            const dimensions = this.backgrounds.contentBox.dimension.getCopy();

            bgGradient.path = createRectanglePath(0, 0, dimensions.getWidth(), dimensions.getHeight());
            bgGradient.contentBox = new Box(new Position(0, 0), dimensions);
            bgGradient.boundingBox = new Box(this.backgrounds.boundingBox.position.getCopy(), dimensions);

            bgGradient.mask = new Mask({
                elementId: this.backgrounds.id as number,
                mode: MaskModeTypes.ALPHA,
            });

            this.bgGradients = new GroupCompElement([bgGradient]);

            this.bgGradients.id = `${this.id}_gradients`;
            this.bgGradients.name = `gradients_${this.bgGradients.id}`;
            this.bgGradients.renderOrder = this.renderOrder;
        }

        this.colorRegions.clear();

        if (textGradientsEls.length) {
            this.gradients = new GroupCompElement(textGradientsEls);

            this.gradients.id = `${this.id}_gradients`;
            this.gradients.name = `gradients_${this.gradients.id}`;
            this.gradients.renderOrder = this.renderOrder;
        }

        const svgDataChildren = [];

        if (this.compSvgLines.length) {
            this.compSvgText = new GroupCompElement(this.compSvgLines);
            this.compSvgText.id = this.id + '_text_' + this.compSvgLines.length;
            this.compSvgText.name = `text_${this.compSvgText.id}`;
            this.compSvgText.renderOrder = this.renderOrder;
            this.compSvgText.requireTempLayer = requireTempLayer;
            this.compSvgText.opacity = this.opacity;
            this.compSvgText.dropShadow = this.dropShadow;
            this.compSvgText.blendMode = this.blendMode;

            svgDataChildren.push(
                ...[
                    this.bgGradients,
                    this.backgrounds,
                    this.backgroundsBorders,
                    this.compSvgText,
                    this.gradients,
                ].filter(Boolean),
            );
        }

        svgDataChildren.push(...this.compOversetBox);

        this.compSvgData = new GroupCompElement(svgDataChildren);

        this.compSvgData.id = this.id; // unique ID
        this.compSvgData.name = `svg_data_${this.compSvgData.id}`;
        this.compSvgData.renderOrder = this.renderOrder;
        this.compSvgData.mask = this.mask;

        return this.compSvgData;
    }

    private findBgIntersections = (lines) => {
        lines.forEach((cur, index) => {
            if (!index || !cur.absBbox.width || !cur.absBbox.height) {
                return;
            }

            const prevIndex = getPrevVisibleLineIndex(lines, index - 1);
            const prev = lines?.[prevIndex];

            if (prev && cur.absBbox.top <= prev.absBbox.top + prev.absBbox.height) {
                const mergeLineIdx = this.bgMergeMap.get(prevIndex) ?? prevIndex;

                if (mergeLineIdx >= 0 && lines[mergeLineIdx]) {
                    this.bgMergeMap.set(index, mergeLineIdx);
                }
            }
        });
    };

    private generateBgCompElements() {
        const lines = getShapedLinesBoxesWithBg(this.boundedText.getShapedLines(), this.textBackground);

        if (!this.textBackground?.isActive() || !lines.length) {
            return [];
        }

        this.findBgIntersections(lines);

        const bgCompElements: ShapeCompElement[] = [];

        lines.forEach((line, lineIdx) => {
            if (!line.absBbox.width || !line.absBbox.height) {
                return;
            }

            const mergeLineIdx = this.bgMergeMap.get(lineIdx) ?? null;
            let bgCompElement;

            if (mergeLineIdx === null) {
                if ([...this.bgMergeMap.values()].includes(lineIdx)) {
                    return;
                }

                bgCompElement = this.getBgCompElement(lineIdx, lineIdx + 1);
            } else {
                const nextLineIndex = getNextVisibleLineIndex(lines, lineIdx + 1);
                const nextLine = lines?.[nextLineIndex];

                if (nextLine && this.bgMergeMap.get(nextLineIndex) !== undefined) {
                    return;
                }

                bgCompElement = this.getBgCompElement(mergeLineIdx, lineIdx + 1);
            }

            if (bgCompElement) {
                bgCompElements.push(bgCompElement);
            }
        });

        return bgCompElements;
    }

    private getOversetCompBox() {
        const compEl = new SvgCompElement();

        compEl.id = `${this.id}_overset_box`; // unique ID
        compEl.color = ORDINARY_STROKE_COLOR_OBJ;
        compEl.path = createRectanglePath(0, 0, this.dimension.width, this.dimension.height);
        compEl.horizontalPathScale = 1;
        compEl.verticalPathScale = 1;
        compEl.isAssetLoading = this.isAssetLoading;
        compEl.renderOrder = this.renderOrder;
        compEl.strokeWidth = 4;
        compEl.strokeType = StrokeType.CENTER;
        compEl.strokeColor = null;
        compEl.punchOut = false;
        compEl.contentBox = new Box(new Position(0, 0), this.dimension.getCopy());
        compEl.boundingBox = new Box(this.position.getCopy(), this.dimension.getCopy());
        compEl.pathPosition = new Position(0, 0);

        return compEl;
    }

    private getSvgCompElement(structuredGlyph: IStructuredGlyph, frameIndex: number, keepGradientColor = false) {
        // convert structured text glyph into SVGCompElement
        const id =
            this.id + '_' + this.compSvgLines.length + '_' + this.compSvgWords.length + '_' + this.compSvgChars.length; // unique ID
        const compEl = this.svgCompElementPool.acquire(id, frameIndex);
        compEl.id = id;

        // gradient color is applied as mask, so we remove it here and create mask element later —
        // unless this element paints its fill gradient onto the glyph, where the mask isn't used
        const color = this.boundedText.getShapedText().getTextPropsProperty('color', structuredGlyph.data.runIdx);
        compEl.color = color.type === 'SOLID' || keepGradientColor ? color : defaultColor;

        compEl.path = this.assetLoader!.makePath(structuredGlyph.data);
        compEl.decorationPaths = structuredGlyph.data.decorationPaths;
        compEl.horizontalPathScale = structuredGlyph.data.pathScale;
        compEl.verticalPathScale = -1 * structuredGlyph.data.pathScale; // char glyphs are defined upside down
        compEl.isAssetLoading = this.isAssetLoading;
        compEl.renderOrder = this.renderOrder;
        compEl.strokeWidth = structuredGlyph.data.strokeWidth;
        compEl.strokeType = structuredGlyph.data.strokeType;
        compEl.strokeColor = structuredGlyph.data.strokeColor || null;
        compEl.strokeGradientRegion = null;
        compEl.colorGradientRegion = null;
        compEl.punchOut = structuredGlyph.data.punchOut;
        compEl.glyph = structuredGlyph.data;

        // Content bbox relative to text element bbox
        // We set 1px with as a nominal value for empty letter in case of textBg -
        // otherwise filter fn just count this element to be not-visible
        const absContentBbox =
            structuredGlyph.isNewLine() && this.textBackground?.isActive()
                ? { ...structuredGlyph.data.absBbox, width: 1 }
                : structuredGlyph.data.absContentBbox;
        const strokeOverflow = compEl.strokeType === StrokeType.OUTSIDE ? compEl.strokeWidth : 0;

        compEl.contentBox = new Box(
            new Position(-strokeOverflow, -strokeOverflow),
            new Dimension(absContentBbox.width + strokeOverflow * 2, absContentBbox.height + strokeOverflow * 2),
        );
        compEl.pathPosition = new Position(
            structuredGlyph.data.absLeftPx - absContentBbox.left + strokeOverflow,
            structuredGlyph.data.absTopPx - absContentBbox.top + strokeOverflow,
        );
        compEl.boundingBox = new Box(
            new Position(this.position.getX() + absContentBbox.left, this.position.getY() + absContentBbox.top),
            new Dimension(absContentBbox.width, absContentBbox.height),
        );

        return compEl;
    }

    private getBgCompElement(lineIdxStart: number, lineIdxEnd: number): ShapeCompElement | null {
        const lines = getShapedLinesBoxesWithBg(
            this.boundedText.getShapedLines().slice(lineIdxStart, lineIdxEnd),
            this.textBackground,
        );

        // TODO: This calculation needs to be changed and refactored
        const getPathPoints = (xOffset = 0, yOffset = 0) => {
            const cordsLeft = [];
            const cordsRight = [];

            lines.forEach((curr, index) => {
                if (curr.absBbox.width <= 0 || curr.absBbox.height <= 0) {
                    return;
                }

                const prev = lines?.[getPrevVisibleLineIndex(lines, index - 1)];
                const next = lines?.[getNextVisibleLineIndex(lines, index + 1)];
                const passSmallLine = prev?.absBbox?.top + prev?.absBbox?.height > next?.absBbox?.top;

                const hasBiggerLineAfter = () =>
                    !!lines.slice(index + 1).some((current) => current?.absBbox?.width > curr.absBbox.width);

                if (index && passSmallLine && hasBiggerLineAfter()) {
                    return;
                }

                let { left, top } = curr.absBbox;
                const { width, height } = curr.absBbox;

                left = left - xOffset;
                top = top - yOffset;

                const topToUse = cordsLeft?.[cordsLeft.length - 1]?.y ?? top;
                const right = left + width;
                const bottom = top + height;

                const fnToApply = next?.absBbox?.width > width ? Math.min : Math.max;
                const bottomToUse = next ? fnToApply(bottom, next.absBbox.top - yOffset) : bottom;

                const radius = (lines[0].absBbox.height / 2) * (this.textBackground.cornerRadius * 0.01);

                cordsLeft.push({ x: left, y: topToUse, r: radius });
                cordsLeft.push({ x: left, y: bottomToUse, r: radius });

                cordsRight.unshift({ x: right, y: topToUse, r: radius });
                cordsRight.unshift({ x: right, y: bottomToUse, r: radius });
            });

            return [...cordsLeft, ...cordsRight];
        };

        const xOffset = Math.min(...lines.map((l) => l.absBbox.left));
        const yOffset = Math.min(...lines.map((l) => l.absBbox.top));

        const points = getPathPoints(xOffset, yOffset);

        if (!points.length) {
            return null;
        }

        const compEl = new ShapeCompElement();

        compEl.path = generateCurvedPath(points);
        compEl.id = `${this.id}_background_${lineIdxStart}_${lineIdxEnd}`;
        compEl.color = this.textBackground.color;
        compEl.borderColor = this.textBackground.borderColor;
        compEl.borderWidth = this.textBackground.borderWidth;
        compEl.borderAlignment = this.textBackground.borderAlignment;
        compEl.borderLineCap = this.textBackground.borderLineCap;
        compEl.isAssetLoading = this.isAssetLoading;
        compEl.renderOrder = this.renderOrder;
        compEl.pathPosition = new Position(0, 0);

        const maxWidth = Math.max(...lines.map((l) => l.absBbox.width));
        const maxHeight =
            lines[lines.length - 1].absBbox.top + lines[lines.length - 1].absBbox.height - lines[0].absBbox.top;
        const dimensions = new Dimension(maxWidth, maxHeight);

        compEl.contentBox = new Box(new Position(xOffset, yOffset), dimensions);
        compEl.boundingBox = new Box(this.position.getCopy(), dimensions);

        const borderWidth = compEl.borderWidth || 0;

        if (borderWidth && compEl.borderAlignment === BorderAlignmentTypes.MIDDLE) {
            const borderOverflow = Math.ceil(borderWidth / 2);
            compEl.pathPosition = new Position(borderOverflow, borderOverflow);
            compEl.contentBox = new Box(
                new Position(xOffset - borderOverflow, yOffset - borderOverflow),
                new Dimension(dimensions.getWidth() + borderOverflow * 2, dimensions.getHeight() + borderOverflow * 2),
            );
        } else if (borderWidth && compEl.borderAlignment === BorderAlignmentTypes.OUTSIDE) {
            const borderOverflow = borderWidth;
            compEl.pathPosition = new Position(borderOverflow, borderOverflow);
            compEl.contentBox = new Box(
                new Position(xOffset - borderOverflow, yOffset - borderOverflow),
                new Dimension(dimensions.getWidth() + borderOverflow * 2, dimensions.getHeight() + borderOverflow * 2),
            );
        }

        return compEl;
    }

    getCompElement(frameIndex: number) {
        let compEl = this.constructCompElement(frameIndex);

        if (compEl === null) {
            return null;
        }

        if ((compEl as any)._boundingBox) {
            const positionDelta = (compEl as any)._position.subtract(this.position);
            compEl.boundingBox = (compEl as any)._boundingBox;
            compEl.boundingBox.position = compEl.boundingBox.position.subtract(positionDelta);
        }

        compEl.originalElement = this;
        compEl.rotation = this.isTextEditorActivated ? 0 : this.rotation;
        compEl.scale = this.scale;
        // ghostContentBox is used here because selecteable area for the textElement
        // is not equal to the contentBox of the element.
        compEl.ghostContentBox = new Box(new Position(0, 0), this.dimension);
        // calculated compEl bounding box behaves like an absolute contentBox, so we have to make position relative
        compEl.contentBox = new Box(compEl.boundingBox.position.subtract(this.position), compEl.contentBox.dimension);
        (compEl as any)._boundingBox = new Box(
            compEl.boundingBox.position.getCopy(),
            compEl.boundingBox.dimension.getCopy(),
        );
        (compEl as any)._position = this.position!.getCopy();
        compEl.boundingBox = new Box(this.position!.getCopy(), this.dimension!.getCopy());

        const isAnimatedFrame = () =>
            this.animationIn?.containsFrame(frameIndex) ||
            this.animationOut?.containsFrame(frameIndex) ||
            this.animations?.some((a) => a.containsFrame(frameIndex));

        const isTextExceedBox = () =>
            this.boundedText.getShapedText().getContentWidth() > this.dimension.width ||
            this.boundedText.getShapedText().getContentHeight() > this.dimension.height;

        if (
            this.limitTextToBounds &&
            !this.isTextEditorActivated &&
            !this.showOversetBox &&
            !isAnimatedFrame() &&
            isTextExceedBox()
        ) {
            this.compSvgData.clipPath = createRectanglePath(
                -compEl.contentBox.position.x,
                -compEl.contentBox.position.y,
                this.dimension.width,
                this.dimension.height,
            );
        } else {
            this.compSvgData.clipPath = null;
        }

        compEl = this.applyAnimations(frameIndex, compEl) as GroupCompElement;

        return compEl;
    }

    // override animations to include text breakup
    applyAnimations(frameIndex: number, compEl: BaseCompElement): BaseCompElement {
        // For ACTIVATED textEditor there will be no animations
        // to give user a possibility see text properly
        // For empty text also don't apply animations
        if (this.isTextEditorActivated || this.compSvgLines.length === 0) {
            return compEl;
        }

        if (this.animations && this.animations.length) {
            this.animations.forEach((animation) => {
                // NOTE: Correct type of the element in update fn
                compEl = animation.updateCompEl(frameIndex, compEl);
            });
        }

        if (this.animationOut) {
            compEl = this.applyTransitionOut(frameIndex, compEl);
        }

        if (this.animationIn) {
            compEl = this.applyTransitionIn(frameIndex, compEl);
        }

        if (this.animationOut || this.animationIn) {
            // Remove compElement items that are marked with removal
            this.cleanCompElementRecursively(compEl);
        }

        return compEl;
    }

    /**
     * Text breakup can cause parts to fall outside of the canvas
     * When motion blur is enabled, this can cause the side-effect that an
     *   invalid translation is applied to parts where the initial position (pre-transform) is the end location.
     * To prevent these side-effects, we remove the invisible elements (marked by the breakup animation functions)
     * NOTE: that these elements are invisible anyway.
     */

    private cleanCompElementRecursively = (compEl: BaseCompElement) => {
        if (compEl instanceof GroupCompElement) {
            const children = compEl.getChildren();

            for (let i = children.length - 1; i >= 0; i--) {
                if ((children[i] as any).remove === true) {
                    children.splice(i, 1);
                } else if (children[i] instanceof GroupCompElement) {
                    this.cleanCompElementRecursively(children[i]);
                }
            }
        }
    };

    private applyTransitionIn(frameIndex: number, compEl: BaseCompElement) {
        if (this.animationIn && this.animationIn.containsFrame(frameIndex)) {
            const animConfig = this.animationIn.config;
            const textBreakup = animConfig.textBreakup;
            let textBreakupDelay = textBreakup?.delay || 0.0;
            let animElements: any[] = [];

            if (textBreakup && textBreakup.type === 'LINE_BREAKUP') {
                animElements = this.compSvgLines;
            } else if (textBreakup && textBreakup.type === 'WORD_BREAKUP') {
                animElements = this.compSvgWords;
            } else if (textBreakup && textBreakup.type === 'CHARACTER_BREAKUP') {
                animElements = this.compSvgChars;
            } else {
                animElements = [this.compSvgData];
                textBreakupDelay = 0.0; // reset because there is no split
            }

            // animation preparations
            const animationDuration = animConfig.duration;
            const animCount = Math.max(1, animElements.length);
            const totalDurations = (animCount - 1) * textBreakupDelay + 1; // total number of full durations (scale of 1 (in case of no delay) to 'groupGount' (in case of 100% delay))

            const animDuration = animationDuration / totalDurations; // calculate the duration for the group to animate

            const animDelay = animDuration * textBreakupDelay; // calculate the delay for a group to start animating

            // determine breakup direction
            if (textBreakup && textBreakup.direction === 'BACKWARD') {
                animElements.reverse();
            }

            const actualStartFrame = this.startFrame;
            const actualAnimDuration = animConfig.duration;
            animConfig.duration = Math.floor(animDuration); // floor to make sure timing function does not overstep boundaries with fractions

            animElements.forEach((animEl, idx) => {
                // adjust duration and startframe of the text element and set the animation
                const animOffsetFrames = Math.floor(animDelay * idx); // floor to make sure no rounding errors occur

                this.startFrame = actualStartFrame + animOffsetFrames;
                // apply animation to group
                createAnimationIn(animConfig, this)?.updateCompEl(frameIndex, animEl);

                if (frameIndex < this.startFrame) {
                    // TODO: hacky way to prevent elements from showing before it is their time.
                    animEl.remove = true;
                }

                animEl.isInAnimation = frameIndex <= this.startFrame + this.duration;
            });

            // restore order
            if (textBreakup && textBreakup.direction === 'BACKWARD') {
                animElements.reverse();
            }

            this.startFrame = actualStartFrame;
            animConfig.duration = actualAnimDuration;
        }

        return compEl;
    }

    private applyTransitionOut(frameIndex: number, compEl: BaseCompElement) {
        if (this.animationOut && this.animationOut.containsFrame(frameIndex)) {
            const animConfig = this.animationOut.config;
            const textBreakup = animConfig.textBreakup;
            let textBreakupDelay = textBreakup?.delay || 0.0;
            let animElements: any[] = [];

            if (textBreakup && textBreakup.type === 'LINE_BREAKUP') {
                animElements = this.compSvgLines;
            } else if (textBreakup && textBreakup.type === 'WORD_BREAKUP') {
                animElements = this.compSvgWords;
            } else if (textBreakup && textBreakup.type === 'CHARACTER_BREAKUP') {
                animElements = this.compSvgChars;
            } else {
                animElements = [this.compSvgData];
                textBreakupDelay = 0.0; // reset because there is no split
            }

            // animation preparations
            const animationDuration = animConfig.duration;
            const animCount = Math.max(1, animElements.length);
            const totalDurations = (animCount - 1) * textBreakupDelay + 1; // total number of full durations (scale of 1 (in case of no delay) to 'groupGount' (in case of 100% delay))

            const animDuration = animationDuration / totalDurations; // calculate the duration for the group to animate

            const animDelay = animDuration * textBreakupDelay; // calculate the delay for a group to start animating

            // determine breakup direction
            if (textBreakup && textBreakup.direction === 'FORWARD') {
                animElements.reverse();
            }

            const actualDuration = this.duration;
            const actualAnimDuration = animConfig.duration;
            animConfig.duration = Math.floor(animDuration); // floor to make sure timing function does not overstep boundaries with fractions
            animElements.forEach((animEl, idx) => {
                // adjust duration and startframe of the text element and set the animation
                const animOffsetFrames = Math.floor(animDelay * idx); // floor to make sure no rounding errors occur

                this.duration = actualDuration - animOffsetFrames;
                // apply animation to group
                createAnimationOut(animConfig, this)?.updateCompEl(frameIndex, animEl);

                if (frameIndex >= this.startFrame + this.duration) {
                    // TODO: hacky way to prevent elements from showing before it is their time
                    animEl.remove = true;
                }

                animEl.isInAnimation = frameIndex <= this.startFrame + this.duration;
            });

            // restore order
            if (textBreakup && textBreakup.direction === 'FORWARD') {
                animElements.reverse();
            }

            this.duration = actualDuration;
            animConfig.duration = actualAnimDuration;
        }

        return compEl;
    }

    private isThereFontsToLoad() {
        const fontsToLoad = getTextPropsFonts(this.formattedText, getFontFamilies());

        return Object.keys(fontsToLoad).some((fontId) => {
            const asset = this.assetLoader!.getAsset(fontId);

            return !asset || (asset as BaseAsset).loading;
        });
    }

    getValidationRules(creativeType: CreativeTypes, previewType: PreviewTypes) {
        const rules = super.getValidationRules(creativeType, previewType);
        const dynamicValidationRules = this.limitTextToBounds
            ? {
                  limitTextToBoundsHeight: {
                      LESS_THAN: this.dimension.height,
                      LESS_THAN_MSG:
                          'Please adjust the height of the bounding box so that the text is contained within it',
                  },
                  limitTextToBoundsWidth: {
                      LESS_THAN: this.dimension.width,
                      LESS_THAN_MSG:
                          'Please adjust the width of the bounding box so that the text is contained within it',
                  },
              }
            : {};

        if (previewType === PreviewTypes.CONTENT) {
            return {
                ...rules,
                ...dynamicValidationRules,
            };
        }

        const maxCharacters = creativeType === CreativeTypes.VIDEO ? VIDEO_MAX_CHARACTERS : IMAGE_MAX_CHARACTERS;

        return {
            ...rules,
            text: {
                MAX_LENGTH: maxCharacters,
            },
            ...dynamicValidationRules,
            // lineHeight: {
            //     GREATER_THAN: 0,
            // },
            // fontSize: {
            //     GREATER_THAN: 1,
            // },
        };
    }

    cleanupEmittingValues(values: Record<string, any>) {
        delete values.textStyles;
        delete values.brandColors;
        delete values.textBackgroundBrandColors;
        super.cleanupEmittingValues(values);
    }

    toObject(): TextElementParams {
        const baseObject = super.toObject();

        return {
            ...baseObject,
            formattedText: this.formattedText,
            textDirection: this.textDirection,
            textControl: this.textControl,
            timelineBehavior: this.timelineBehavior,
            contentTransform: this.contentTransform?.toObject() ?? null,
            limitTextToBounds: this.limitTextToBounds,
            textBackground: this.textBackground?.toObject() ?? null,
            textEditorData: deepClone(this.textEditorData),
            isTextEditorActivated: this.isTextEditorActivated,
            minFontScale: this.minFontScale,
            fontScale: this.fontScale,
            leadingType: this.leadingType,
            textStyles: this.textStyles,
            brandColors: this.brandColors,
            textBackgroundBrandColors: this.textBackgroundBrandColors,
        };
    }
}
